import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import ClientDashboardNavbar from '@/pages/client/dashboard/components/ClientDashboardNavbar';
import { creditWallet, debitWallet, getBalance } from '@/lib/wallet';
import { formatMoney, generateUsername, generatePassword, TX_TYPE_META, txSignedAmount } from '@/lib/credit';

interface TargetAccount {
  id: number;
  name: string;
  nomcommerce: string;
  image: string;
  typecompte: number;
  monaie: string;
  telephone: string;
}

interface LedgerRow {
  id: number;
  type: 'credit' | 'payment' | 'deposit' | 'adjustment';
  amount: number;
  description: string | null;
  created_at: string;
}

type ActionKind = 'send' | 'payDebt' | 'request' | null;

export default function ClientScanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const rawType = (params.get('type') || '').toLowerCase();
  const targetId = parseInt(params.get('id') || '', 10);

  const isCommerce = rawType === 'merchant' || rawType === 'vendeur';

  const [target, setTarget] = useState<TargetAccount | null>(null);
  const [myBalance, setMyBalance] = useState(0);
  const [ledger, setLedger] = useState<LedgerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [action, setAction] = useState<ActionKind>(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; msg: string } | null>(null);

  const showFeedback = (ok: boolean, msg: string) => {
    setFeedback({ ok, msg });
    setTimeout(() => setFeedback(null), 4500);
  };

  const load = useCallback(async () => {
    if (!user || !Number.isFinite(targetId)) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [targetRes, balanceRes] = await Promise.all([
        supabase
          .from('users')
          .select('id, name, nomcommerce, image, typecompte, monaie, telephone')
          .eq('id', targetId)
          .maybeSingle(),
        getBalance(user.id),
      ]);
      setMyBalance(Number(balanceRes) || 0);

      if (!targetRes.data) {
        setNotFound(true);
      } else {
        setTarget(targetRes.data as TargetAccount);
      }

      if (isCommerce) {
        const { data: cc } = await supabase
          .from('credit_customers')
          .select('id')
          .eq('merchant_id', targetId)
          .eq('linked_user_id', user.id)
          .maybeSingle();
        if (cc?.id) {
          const { data: txs } = await supabase
            .from('credit_transactions')
            .select('id, type, amount, description, created_at')
            .eq('customer_id', cc.id)
            .order('created_at', { ascending: false })
            .limit(50);
          setLedger((txs || []) as LedgerRow[]);
        }
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [user, targetId, isCommerce]);

  useEffect(() => {
    load();
  }, [load]);

  const myDebt = useMemo(
    () => ledger.reduce((sum, tx) => sum + txSignedAmount(tx), 0),
    [ledger]
  );

  if (!user) {
    navigate('/login-client', { replace: true });
    return null;
  }

  const currency = user.monaie || 'MAD';
  const displayName = target ? (isCommerce ? target.nomcommerce || target.name : target.name) : '';

  // Trouve (ou crée) la fiche carnet de crédit du client chez ce commerce.
  const resolveCreditCustomer = async (): Promise<number | null> => {
    const { data } = await supabase
      .from('credit_customers')
      .select('id')
      .eq('merchant_id', targetId)
      .eq('linked_user_id', user.id)
      .maybeSingle();
    if (data?.id) return data.id as number;
    const { data: created, error } = await supabase
      .from('credit_customers')
      .insert({
        merchant_id: targetId,
        full_name: user.name || user.user_name,
        phone: user.telephone || null,
        linked_user_id: user.id,
        status: 'active',
        username: generateUsername(user.name || user.user_name),
        temp_password: generatePassword(),
      })
      .select('id')
      .single();
    if (error) return null;
    return (created?.id as number) ?? null;
  };

  const doSend = async (amt: number) => {
    if (amt > myBalance) {
      showFeedback(false, `Solde portefeuille insuffisant (${formatMoney(myBalance, currency)}).`);
      return;
    }
    const debit = await debitWallet(user.id, amt, 'transfert', note || `Envoi à ${displayName}`);
    if (!debit.success) {
      showFeedback(false, debit.error || "Échec de l'envoi.");
      return;
    }
    const credit = await creditWallet(targetId, amt, 'transfert', note || `Reçu de ${user.name}`);
    if (!credit.success) {
      // Tentative de remboursement pour éviter une perte.
      await creditWallet(user.id, amt, 'refund', 'Annulation transfert échoué');
      showFeedback(false, "Le destinataire n'a pas pu être crédité. Opération annulée.");
      return;
    }
    await supabase.from('transfertwallet').insert({ id_sender: user.id, id_receiver: targetId, somme: String(amt) });
    setMyBalance((b) => b - amt);
    showFeedback(true, `${formatMoney(amt, currency)} envoyés à ${displayName}.`);
  };

  const doPayDebt = async (amt: number) => {
    const customerId = await resolveCreditCustomer();
    if (customerId == null) {
      showFeedback(false, 'Impossible de rattacher votre compte à ce commerce.');
      return;
    }
    const { error } = await supabase.from('credit_transactions').insert({
      merchant_id: targetId,
      customer_id: customerId,
      type: 'payment',
      amount: amt,
      description: note || 'Paiement depuis le portefeuille Zifek',
      created_by: user.id,
    });
    if (error) {
      showFeedback(false, "Échec de l'enregistrement du paiement.");
      return;
    }
    setLedger((prev) => [
      { id: -Date.now(), type: 'payment', amount: amt, description: note || 'Paiement', created_at: new Date().toISOString() },
      ...prev,
    ]);
    showFeedback(true, `Paiement de ${formatMoney(amt, currency)} enregistré chez ${displayName}.`);
  };

  const doRequest = async (amt: number) => {
    const { error } = await supabase.from('notifications').insert({
      user_id: targetId,
      sender_id: user.id,
      publication_id: 0,
      type: 'money_request',
      message: `${user.name} vous demande ${formatMoney(amt, currency)}${note ? ` — ${note}` : ''}`,
      is_read: 0,
    });
    if (error) {
      showFeedback(false, "La demande n'a pas pu être envoyée.");
      return;
    }
    showFeedback(true, `Demande de ${formatMoney(amt, currency)} envoyée à ${displayName}.`);
  };

  const submit = async () => {
    const amt = Number(String(amount).replace(',', '.'));
    if (!Number.isFinite(amt) || amt <= 0) {
      showFeedback(false, 'Montant invalide.');
      return;
    }
    setBusy(true);
    try {
      if (action === 'send') await doSend(amt);
      else if (action === 'payDebt') await doPayDebt(amt);
      else if (action === 'request') await doRequest(amt);
      setAction(null);
      setAmount('');
      setNote('');
    } finally {
      setBusy(false);
    }
  };

  const actionMeta: Record<Exclude<ActionKind, null>, { title: string; hint: string; submit: string; icon: string }> = {
    send: {
      title: "Envoyer de l'argent",
      hint: `Les fonds seront prélevés sur votre portefeuille Zifek et envoyés à ${displayName}.`,
      submit: 'Envoyer',
      icon: 'ri-send-plane-fill',
    },
    payDebt: {
      title: 'Payer une dette',
      hint: `Le paiement sera enregistré dans le carnet de crédit de ${displayName}.`,
      submit: 'Payer',
      icon: 'ri-hand-coin-line',
    },
    request: {
      title: "Demander de l'argent",
      hint: `${displayName} recevra une demande de paiement.`,
      submit: 'Envoyer la demande',
      icon: 'ri-hand-heart-line',
    },
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <ClientDashboardNavbar />
      <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 max-w-3xl mx-auto w-full">
        <Link
          to="/mon-compte"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors no-underline mb-4"
        >
          <i className="ri-arrow-left-line"></i>Mon espace
        </Link>

        {loading ? (
          <div className="flex items-center justify-center py-24">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
          </div>
        ) : notFound || !target ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <i className="ri-qr-scan-2-line text-4xl text-foreground-300 mb-3"></i>
            <p className="text-sm text-foreground-600 mb-4">Compte Zifek introuvable.</p>
            <Link to="/mon-compte" className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline">Retour</Link>
          </div>
        ) : (
          <>
            {/* Profil scanné */}
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-5 mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {target.image ? (
                    <img src={target.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-lg font-bold">{displayName.charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <h1 className="text-lg font-bold font-heading text-foreground-950 truncate">{displayName}</h1>
                  <span className={`inline-flex items-center gap-1.5 mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    isCommerce ? 'bg-secondary-100 text-secondary-700' : 'bg-accent-100 text-accent-700'
                  }`}>
                    <i className={isCommerce ? 'ri-store-2-line' : 'ri-user-3-line'}></i>
                    {isCommerce ? 'Commerce Zifek' : 'Compte client Zifek'}
                  </span>
                </div>
              </div>
            </div>

            {/* Solde portefeuille */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                <div className="flex items-center gap-2 text-foreground-400 mb-1">
                  <i className="ri-wallet-3-line text-sm"></i>
                  <span className="text-[11px] font-medium uppercase tracking-wider">Mon portefeuille</span>
                </div>
                <div className="text-lg font-bold font-heading text-foreground-950">{formatMoney(myBalance, currency)}</div>
              </div>
              {isCommerce && (
                <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-foreground-400 mb-1">
                    <i className="ri-book-2-line text-sm"></i>
                    <span className="text-[11px] font-medium uppercase tracking-wider">Ma dette chez ce commerce</span>
                  </div>
                  <div className={`text-lg font-bold font-heading ${myDebt > 0 ? 'text-primary-700' : 'text-accent-600'}`}>
                    {formatMoney(Math.abs(myDebt), currency)}
                    {myDebt < 0 && <span className="text-xs font-normal text-accent-600 ml-1">(dépôt)</span>}
                  </div>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 mb-6">
              <button
                onClick={() => setAction('send')}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-send-plane-fill"></i>Envoyer de l&apos;argent
              </button>
              {isCommerce && (
                <button
                  onClick={() => setAction('payDebt')}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-hand-coin-line"></i>Payer une dette
                </button>
              )}
              {!isCommerce && (
                <button
                  onClick={() => setAction('request')}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap bg-secondary-500 text-background-50 hover:bg-secondary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-hand-heart-line"></i>Demander de l&apos;argent
                </button>
              )}
            </div>

            {/* Activité (commerce) */}
            {isCommerce && (
              <div>
                <h2 className="text-sm font-semibold text-foreground-900 mb-3">Activité avec {displayName}</h2>
                {ledger.length === 0 ? (
                  <div className="py-10 text-center bg-background-50 border border-background-200/70 rounded-lg">
                    <i className="ri-history-line text-2xl text-foreground-300"></i>
                    <p className="mt-2 text-sm text-foreground-500">Aucune opération pour le moment</p>
                  </div>
                ) : (
                  <div className="bg-background-50 border border-background-200/70 rounded-lg divide-y divide-background-200/70 overflow-hidden">
                    {ledger.map((tx) => {
                      const meta = TX_TYPE_META[tx.type];
                      const signed = txSignedAmount(tx);
                      return (
                        <div key={tx.id} className="flex items-center gap-3 px-4 py-3">
                          <div className={`w-9 h-9 rounded-full ${meta.className} flex items-center justify-center flex-shrink-0`}>
                            <i className={`${meta.icon} text-base`}></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-foreground-900 truncate">
                              {meta.label}{tx.description ? ` · ${tx.description}` : ''}
                            </div>
                            <div className="text-[11px] text-foreground-400">
                              {new Date(tx.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </div>
                          </div>
                          <div className={`text-sm font-bold flex-shrink-0 ${signed < 0 ? 'text-accent-600' : 'text-primary-700'}`}>
                            {signed < 0 ? '-' : '+'}{formatMoney(Math.abs(signed), currency)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Modale d'action */}
      {action && target && (
        <>
          <div className="fixed inset-0 bg-black/40 z-50" onClick={() => !busy && setAction(null)} />
          <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
            <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70">
              <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
                <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
                  <i className={`${actionMeta[action].icon} text-primary-500`}></i>{actionMeta[action].title}
                </h3>
                <button
                  onClick={() => !busy && setAction(null)}
                  className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-foreground-500"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <p className="text-xs text-foreground-500 leading-relaxed flex items-start gap-2">
                  <i className="ri-information-line text-primary-500 mt-0.5"></i>
                  <span>{actionMeta[action].hint}</span>
                </p>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Montant ({currency})</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
                    placeholder="0"
                    autoFocus
                    className="w-full h-12 px-4 rounded-lg border border-background-200/70 bg-background-50 text-lg font-semibold text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Motif (optionnel)</label>
                  <input
                    type="text"
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    maxLength={120}
                    placeholder="Ex. Remboursement"
                    className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                  />
                </div>
              </div>
              <div className="p-4 border-t border-background-200/70 flex gap-2">
                <button
                  onClick={() => !busy && setAction(null)}
                  className="flex-1 h-11 rounded-full bg-background-100 border border-background-200/70 text-sm font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={submit}
                  disabled={busy}
                  className="flex-1 h-11 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                  {actionMeta[action].submit}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {feedback && (
        <div
          className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-4 py-2.5 rounded-full text-xs font-medium whitespace-nowrap ${
            feedback.ok ? 'bg-primary-600 text-background-50' : 'bg-accent-600 text-background-50'
          }`}
        >
          {feedback.msg}
        </div>
      )}
    </div>
  );
}